'use client';

import { Button, Link, Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode, useEffect, useId, useRef, useState } from 'react';
import { AuthenticatorCodeInput } from '../AuthenticatorCodeInput/AuthenticatorCodeInput';
import { isValidAuthenticatorCode } from '../PasswordlessLogin/authenticatorCode';
import { useThrottledResend } from '../ThrottledResend/useThrottledResend';
import { ResendReserve } from './ResendReserve';

export type LinkSentSignInResendProps = Readonly<{
  /** Slot 16, e.g. "Didn't get it?" - holds across every state, ahead of whichever of
   *  `resendActionLabel`/`codePrompt` currently follows it (see rule 6: the two never appear
   *  together). */
  leadLine: ReactNode;
  /** Slot 18, e.g. "Resend the email" - the pressable words, shown until a press past the 60
   *  second window reveals the code ask, at which point slot 19 (`codePrompt`) replaces it
   *  inline and this stops being a control at all. */
  resendActionLabel: ReactNode;
  /** Slot 31, e.g. "Please try again later, we will let you know." - see LinkSentResend's own doc
   *  comment for the shared reasoning. The caller supplies the whole node directly; there is no
   *  number for this component to receive or interpolate. */
  pressedInsideWindowMessage: ReactNode;
  /** Slot 28 - shown at the bottom once the window has elapsed, whether or not the code ask is
   *  revealed. One of this composition's main reserve's three occupants (see ResendReserve), not a
   *  distinguished one. */
  replacementWarning: ReactNode;
  /** Slot 34 - shown once the window has elapsed for a customer who DID press inside it (see the
   *  component doc comment for the four-state table): the answer `pressedInsideWindowMessage`
   *  gave was promising something, and this keeps that promise before repeating slot 28 verbatim,
   *  same as LinkSentResend's own `resendNowMessage` (see that file's own doc comment for the full
   *  reasoning - shared verbatim here, not repeated). Cleared the same way every other occupant
   *  here is: not by the window merely elapsing, but by a genuinely past-window press, which on
   *  THIS composition means `handleLeadPress` revealing the code ask rather than sending (see the
   *  component doc comment for why this component's press semantics differ from
   *  LinkSentResend's). */
  resendNowMessage: ReactNode;
  /** Slot 19, e.g. "Enter the code from your authenticator app." - replaces `resendActionLabel`
   *  inline once revealed, and is also the six code boxes' own accessible name (this component
   *  points AuthenticatorCodeInput's `aria-labelledby` at the element rendering this text - see
   *  that component's own doc comment for why it takes an id rather than a second label prop). */
  codePrompt: ReactNode;
  /** Slot 20, e.g. "Resend" - the button that actually submits the fresh code. The only greyed
   *  control anywhere on this screen (rule 8): disabled purely by whether six valid digits have
   *  been typed, never by the clock. */
  resendButtonLabel: ReactNode;
  /** Slot 32, e.g. "Code not accepted." - shown in the held error space under the code boxes when
   *  `error` is true. That reserve's only occupant (see ResendReserve): held even when nothing is
   *  live, so the space is reserved from first paint. */
  codeRejectedMessage: ReactNode;
  /** Whether the fresh code most recently submitted was refused by the backend. This component
   *  never calls the API (ADR-0050) and so never learns this on its own - see the `isResending`
   *  doc below for the protocol it watches to find out. Caller-owned: the caller clears it on the
   *  next press, mirroring `magicLinkPendingPressFailed`/`resendSignInLinkFailed`, the boolean
   *  "should I show it right now" flags `cognito-passwordless-auth`'s own PasswordlessLogin.tsx
   *  container already uses for the sibling MAGIC_LINK_PENDING screen's own press-failure state.
   *
   *  **Caller contract, load-bearing, not optional style**: this must be settled (either left
   *  `false` or set `true`) in the SAME render that drops `isResending` back to `false` - see
   *  `isResending`'s own doc for why the collapse-vs-stay-open decision is read at that exact
   *  transition, and why a caller that resolves the two across separate renders (or synchronously,
   *  skipping `isResending` altogether) breaks the contract silently: the code ask then never
   *  collapses on a genuine success, because this component never sees the edge it is watching
   *  for. `cognito-passwordless-auth`'s PasswordlessLogin.tsx, which wires the real sign-in view
   *  onto this prop pair (Task 5), honours this: `resendSignInLinkFailed` and
   *  `isResendingSignInLink` are set from the same `performSignIn` promise settling, one render
   *  apart from each other exactly as this contract requires. */
  error?: boolean;
  /** Called with the fresh code once the customer presses Resend, having already passed both
   *  gates (a well-formed code, not already resending). Fired directly from that press - this
   *  component never awaits a result and never restarts its own timer here (see the component
   *  doc comment for why the 60 second throttle governs only the reveal press, not this one). */
  onResend: (code: string) => void;
  /** Forwarded to useThrottledResend, which gates only the reveal press; see that hook's own doc
   *  for where the 60-second default comes from. */
  throttleSeconds?: number;
  /** True while a submitted code's mint request is in flight. This is also the signal this
   *  component waits on to learn what happened to the LAST submission: the transition from true
   *  back to false is read, together with `error` at that same moment, as "that attempt is
   *  resolved" - see the component doc comment for the full protocol. A caller that never sets
   *  this stays in whatever state the customer last put it in; the code ask does not have a
   *  safe way to guess when the customer would want it collapsed instead.
   *
   *  **Caller contract, load-bearing**: a genuine true -> false transition has to actually reach
   *  this component as two separate renders (`isResending` true, then a later render with it
   *  false) for the edge to be observed at all. A caller that resolves synchronously or
   *  optimistically - setting `isResending` straight back to `false` in the same tick it was set,
   *  or never setting it `true` in the first place - never produces that edge, and the code ask
   *  then silently never collapses on success, indistinguishable from "still waiting" from this
   *  component's point of view. This is the safest contract available under ADR-0050 (no API
   *  access here to learn the real outcome any other way), but it is a real constraint on whatever
   *  wires the API call to this prop - Task 5, most immediately. */
  isResending?: boolean;
  'data-testid'?: string;
}>;

/**
 * Fills LinkSent's `resend` slot on the returning-customer sign-in route, which cannot simply
 * repeat the request that sent the first link the way LinkSentResend's three routes can: the
 * backend wants a fresh, live six-digit code from the authenticator app, because every sign-in
 * link is its own possession check. See LinkSentResend's own doc comment for the four states
 * this shares with every other route (nothing, the pressed-inside-window answer, the resend-now
 * answer, the past-the-window warning) - this component only diverges once a press lands past the
 * window.
 *
 * Two things happen on that press, and the design board this replaces (screen "2b") got both
 * wrong by conflating them: it revealed the code field automatically at the 60-second mark
 * (rule 5 forbids this - a control substituting itself unprompted, mid-read, is worse than the
 * field simply not being there yet), and once revealed the field never left, so a customer who
 * resent was left staring at a code ask the backend would refuse for another minute. Here, the
 * reveal happens only on a press (`handleLeadPress` below, gated on `!isCountingDown`), and it
 * collapses again once a submission resolves without being rejected (see the isResending/error
 * protocol below).
 *
 * `useThrottledResend`'s own `handleResend` is never called *from a press*: the RESEND button's
 * submit calls `onResend` directly instead, bypassing the hook entirely for that click - a
 * rejected code must be retriable immediately, not throttled by the same 60-second mint window a
 * genuinely accepted one restarts, so routing the submit through the hook's unconditional
 * restart-on-press would silently lock a customer out of correcting a typo for a full minute.
 * `handleResend` IS still called once, but only from the isResending/error edge effect below, and
 * only on the accepted branch - seeded with a no-op `onResend` (see where the hook is constructed)
 * so that call restarts the countdown without firing a second real send on top of the one already
 * sent directly. Earlier draft of this component used the hook purely as a read-only clock and
 * never called `handleResend` at all: that left `isCountingDown` permanently false from the first
 * time the window ever elapsed (nothing else in `useThrottledResend` ever resets its deadline), so
 * a customer who sent successfully and then pressed resend again immediately sailed straight into
 * the backend's own opaque 401/500 with no client-side throttle at all. Fixed by restarting the
 * countdown on exactly the one edge that means "a link genuinely just went out": the same fact
 * that makes the *other* routes restart theirs.
 *
 * The protocol for "what happened to the code I just submitted": this component has no API access
 * (ADR-0050) and no callback for "that succeeded", only `isResending` and `error` - see both
 * props' own doc comments for the caller contract this depends on (a genuine two-render
 * true-then-false transition; a caller that resolves synchronously or optimistically breaks it
 * silently). It watches the edge where `isResending` drops from true to false: with `error` false
 * at that moment, the submission is read as accepted - the code ask collapses back to the resting
 * state (rule 5), clears the one-time code, AND restarts the client throttle (see above); with
 * `error` true, it stays open, showing the rejection, with whatever the customer typed still in
 * the boxes untouched and the throttle left exactly as it was - exactly the "Email sent, code
 * rejected" board state, where nothing was spent (no new link exists, the backend's own window
 * never restarted either) and the customer only needs to retype one digit, not the whole code. A
 * caller that never toggles `isResending` never sees the collapse fire, which is the safe default:
 * this component cannot tell success from "still waiting" on its own, so it does not guess.
 *
 * The edge is additionally gated on `isRevealed`: `isResending`'s own contract ("true only while a
 * submitted code's mint request is in flight") is a caller convention this component cannot
 * enforce, so a future caller toggling it for an unrelated in-flight request while the code ask
 * is not even on screen must not be read as a send either - defence in depth against a quieter
 * version of the exact bug this effect exists to fix.
 *
 * Wrapped in withClarityTheme for the same reason LinkSentResend is: the resend action's colour
 * only exists on the Clarity theme.
 */
function LinkSentSignInResendComponent({
  leadLine,
  resendActionLabel,
  pressedInsideWindowMessage,
  replacementWarning,
  resendNowMessage,
  codePrompt,
  resendButtonLabel,
  codeRejectedMessage,
  error,
  onResend,
  throttleSeconds,
  isResending,
  'data-testid': dataTestId,
}: LinkSentSignInResendProps): ReactElement {
  const [wasPressedInsideWindow, setWasPressedInsideWindow] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [freshCode, setFreshCode] = useState('');
  const codePromptId = useId();

  const { isCountingDown, handleResend } = useThrottledResend({
    // Never called from a press: this composition's own RESEND button calls `onResend` directly
    // instead (see the component doc comment for why the code submit bypasses this hook's own
    // handleResend for THAT). This no-op is only ever reached the other way - `handleResend` is
    // called once, below, purely to restart the countdown after a confirmed-accepted send, and
    // that call must not fire a second real send on top of the one already sent directly.
    onResend: () => {},
    throttleSeconds,
  });

  // Detects the isResending true -> false edge described in the component doc comment. A ref,
  // not a dependency-driven flag, so a caller passing a fresh isResending value that happens to
  // already equal the previous one (or re-rendering for an unrelated reason) cannot re-fire this.
  const wasResendingRef = useRef(isResending);
  useEffect(() => {
    const wasResending = wasResendingRef.current;
    wasResendingRef.current = isResending;
    if (!wasResending || isResending) {
      return;
    }
    // Gated on isRevealed: isResending's own contract ("true only while a submitted code's mint
    // request is in flight") is a caller convention, not something this component can enforce -
    // nothing stops a future caller toggling isResending for some unrelated in-flight request
    // while the code ask is not even on screen. Without this gate, that edge would still reach
    // the branch below and restart the countdown with no code ever submitted: a quieter version
    // of the exact bug this effect exists to fix, the client throttle moving for a reason that is
    // not a send. The code ask being revealed is the only fact that actually means "a submission
    // could be in flight", so it is the gate, not isResending/error alone.
    if (!isRevealed) {
      return;
    }
    if (error) {
      // Rejected: nothing was spent, the old link still works, and the backend's own throttle
      // never restarted either - see the component doc comment for why this deliberately leaves
      // the countdown alone, so a corrected code can be retried immediately.
      return;
    }
    // Accepted: the customer has a fresh link in the inbox and the backend's own 60 second mint
    // window has genuinely restarted, so the client one must too - exactly the same restart
    // ThrottledResend.tsx's own button gets on every press, reached here via the same
    // `handleResend` rather than a second, independent timer. Safe to call: this branch is only
    // ever reached once `isCountingDown` is already false (revealing the code ask in the first
    // place required that - see handleLeadPress below - and nothing between then and here can set
    // it true again except this very call), so the hook's own disabled guard can never block it.
    setIsRevealed(false);
    setFreshCode('');
    handleResend();
  }, [isResending, error, isRevealed, handleResend]);

  function handleLeadPress() {
    if (isCountingDown) {
      setWasPressedInsideWindow(true);
      return;
    }
    // Past the window, not yet revealed: this press only reveals the code ask. It must not
    // itself send - see the component doc comment for the "2b" mistake this supersedes.
    setWasPressedInsideWindow(false);
    setIsRevealed(true);
  }

  function handleSubmitCode() {
    if (isResending || !isValidAuthenticatorCode(freshCode)) {
      return;
    }
    onResend(freshCode);
  }

  const isSubmitDisabled = Boolean(isResending) || !isValidAuthenticatorCode(freshCode);

  return (
    <Stack
      data-testid={dataTestId}
      alignItems="center"
      sx={(theme: Theme) => ({ gap: theme.semantic.spacing.sm, width: '100%' })}
    >
      <Typography variant="body2" color="text.tertiary">
        {leadLine}{' '}
        {isRevealed ? (
          <span id={codePromptId}>{codePrompt}</span>
        ) : (
          <Link
            component="button"
            type="button"
            underline="none"
            data-action="resend"
            onClick={handleLeadPress}
            sx={(theme: Theme) => ({
              font: 'inherit',
              color: theme.palette.permanent.primary,
              fontWeight: theme.semantic.fontWeight.semibold,
              cursor: 'pointer',
            })}
          >
            {resendActionLabel}
          </Link>
        )}
      </Typography>

      {!isRevealed && (
        <ResendReserve
          data-testid="resend-reserve"
          occupants={[
            {
              key: 'replacement-warning',
              content: (
                <Typography variant="body2" color="text.tertiary">
                  {replacementWarning}
                </Typography>
              ),
            },
            {
              key: 'pressed-inside-window',
              content: (
                <Typography variant="body2" color="text.tertiary">
                  {pressedInsideWindowMessage}
                </Typography>
              ),
            },
            {
              key: 'resend-now',
              content: (
                <Typography variant="body2" color="text.tertiary">
                  {resendNowMessage}
                </Typography>
              ),
            },
          ]}
          liveKey={
            isCountingDown
              ? wasPressedInsideWindow
                ? 'pressed-inside-window'
                : null
              : wasPressedInsideWindow
                ? 'resend-now'
                : 'replacement-warning'
          }
        />
      )}

      {isRevealed && (
        <>
          <AuthenticatorCodeInput
            value={freshCode}
            onChange={setFreshCode}
            aria-labelledby={codePromptId}
            error={error}
            disabled={isResending}
          />
          <ResendReserve
            data-testid="code-rejected-reserve"
            occupants={[
              {
                key: 'code-rejected',
                content: (
                  <Typography variant="body2" color="text.danger">
                    {codeRejectedMessage}
                  </Typography>
                ),
              },
            ]}
            liveKey={error ? 'code-rejected' : null}
          />
          <Button
            type="button"
            variant="outlined"
            color="primary"
            data-action="resend"
            disabled={isSubmitDisabled}
            onClick={handleSubmitCode}
            sx={(theme: Theme) => ({
              // MUI's outlined variant sets its border at 50% alpha of the palette colour
              // (`rgba(...primary.main, 0.5)`) for any non-`inherit` colour, with no Clarity
              // theme override to correct it (confirmed: no `MuiButton` override exists). The
              // board's own stylesheet draws a solid, full-alpha stroke here
              // (`--button-secondary-border`, measured with getComputedStyle against the live
              // board rather than assumed from the sheet alone - see this component's own
              // Storybook play test), the same colour as the lead words beside it, so the
              // default 50% tint is overridden to the theme's real (full-alpha) primary colour.
              borderColor: theme.palette.primary.main,
              // Disabled specifically renders a DIFFERENT colour on the board, not a faded
              // version of the one above: loader.css's own `.stack .btn.secondary.disabled`
              // override (measured the same way) swaps the border to `--border-default`
              // (`#c5c6c9`), the exact value `theme.palette.grey['200']` already carries for the
              // code boxes' own border (AuthenticatorCodeInput.tsx) - reused here rather than a
              // second literal for the same value. That override's own comment explains why: a
              // disabled secondary button that kept the live eggplant stroke "reads as
              // pressable", which is the opposite of what disabled means.
              '&.Mui-disabled': {
                borderColor: theme.palette.grey['200'],
              },
            })}
          >
            {resendButtonLabel}
          </Button>
          <Typography variant="body2" color="text.tertiary">
            {replacementWarning}
          </Typography>
        </>
      )}
    </Stack>
  );
}

export const LinkSentSignInResend = withClarityTheme(LinkSentSignInResendComponent);
